import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { SignaturePad } from "../components/SignaturePad";
import api, { apiError, AED } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Loader2, CheckCircle2, ClipboardCheck, PenLine, UserRound, FileCheck2, AlertTriangle } from "lucide-react";
import dayjs from "dayjs";

const STEPS = [
  { id: 1, label: "Driver details", icon: UserRound },
  { id: 2, label: "Rental terms", icon: FileCheck2 },
  { id: 3, label: "Signature", icon: PenLine },
];

const stepCircleClass = (step, id) => {
  if (step > id) return "bg-[#009900] border-[#009900] text-white";
  if (step === id) return "border-[#009900] text-[#009900]";
  return "border-[#d5ddd5]";
};

const TERMS = [
  "Vehicle is rented per the confirmed category — the exact model may vary (\"or similar\" policy).",
  "Minimum driver age is 21 (25 for premium vehicles), and your licence must have been held for at least 12 months. No young-driver fee applies.",
  "Included mileage per the booking; excess mileage charged at AED 0.50–0.75/km + 5% VAT per tier.",
  "Security deposit: credit card pre-authorization or debit card charge as disclosed at checkout (waived with NDW).",
  "Fuel policy: return with the same fuel level as at handover; toll crossings (Salik/Darb) are billed after your rental.",
  "Traffic fines incurred during the rental period are the renter's responsibility plus an admin fee.",
  "Report any accident immediately via 800-EUROPCAR and file a police report where required by UAE law.",
];

export default function CheckIn() {
  const { ref } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState(null);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ license_no: "", nationality: "", date_of_birth: "" });
  const [accepted, setAccepted] = useState(false);
  const [signature, setSignature] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (user === null) navigate(`/login?next=/checkin/${ref}`);
  }, [user, navigate, ref]);

  useEffect(() => {
    if (!user) return;
    api.get(`/bookings/${ref}`)
      .then((r) => {
        setBooking(r.data);
        setForm((f) => ({ ...f, license_no: user.license_no || "", nationality: user.nationality || "" }));
      })
      .catch((e) => setError(apiError(e)));
  }, [ref, user]);

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post(`/bookings/${ref}/checkin`, { ...form, accept_terms: accepted, signature });
      setResult(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const shell = (content) => (
    <div className="min-h-screen flex flex-col bg-[#fafcfa]">
      <Header />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10 w-full flex-1">{content}</main>
      <Footer />
    </div>
  );

  if (!user || (!booking && !error)) return shell(<div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-[#009900]" /></div>);

  if (error)
    return shell(
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-8 text-center" data-testid="checkin-error">
        <AlertTriangle className="w-8 h-8 text-amber-600 mx-auto mb-3" />
        <p className="text-sm text-amber-800">{error}</p>
        <Link to="/account" className="btn-cta rounded px-5 py-2.5 text-xs inline-block mt-4">Back to My Bookings</Link>
      </div>
    );

  if (result || booking.checkin?.status === "completed") {
    const ra = result?.ra_number || booking.checkin.ra_number;
    return shell(
      <div className="text-center fade-up" data-testid="checkin-success">
        <CheckCircle2 className="w-16 h-16 text-[#009900] mx-auto mb-4" />
        <h1 className="heading-upper text-2xl sm:text-3xl text-[#144733]">Check-in complete</h1>
        <p className="text-sm text-[#57685e] mt-2">Your digital rental agreement is signed and ready. Show your booking reference at pickup or delivery.</p>
        <div className="inline-block bg-[#144733] text-white rounded-lg px-8 py-4 mt-6">
          <div className="text-[10px] uppercase tracking-widest text-[#FFDC00]">Rental agreement</div>
          <div className="text-2xl font-bold tracking-wider" data-testid="ra-number">{ra}</div>
          <div className="text-xs text-white/60 mt-1">Booking {booking.booking_ref}</div>
        </div>
        <div className="mt-8">
          <Link to="/account" className="btn-cta rounded px-6 py-3 text-sm inline-block" data-testid="checkin-back-account-btn">Back to My Bookings</Link>
        </div>
      </div>
    );
  }

  if (booking.status === "cancelled" || booking.display_status === "completed")
    return shell(
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-8 text-center" data-testid="checkin-unavailable">
        <AlertTriangle className="w-8 h-8 text-amber-600 mx-auto mb-3" />
        <p className="text-sm text-amber-800">Online check-in is not available for this booking ({booking.display_status}).</p>
        <Link to="/account" className="btn-cta rounded px-5 py-2.5 text-xs inline-block mt-4">Back to My Bookings</Link>
      </div>
    );

  const canNext1 = form.license_no.trim() && form.nationality.trim();

  return shell(
    <div data-testid="checkin-page">
      <div className="promo-stripe mb-6">
        <h1 className="heading-upper text-2xl text-[#144733] flex items-center gap-2"><ClipboardCheck className="w-6 h-6 text-[#009900]" /> Online check-in</h1>
        <p className="text-sm text-[#57685e] mt-1">
          {booking.vehicle.name} · {booking.booking_ref} · pickup {dayjs(booking.pickup_datetime).format("DD MMM, HH:mm")} · total {AED(booking.quote.total)}
        </p>
      </div>

      <div className="flex items-center gap-0 mb-8" data-testid="checkin-steps">
        {STEPS.map((s, i) => (
          <div key={s.id} className="flex items-center flex-1 last:flex-none">
            <div className={`flex items-center gap-2 ${step >= s.id ? "text-[#009900]" : "text-[#9aa79f]"}`}>
              <span className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border-2 ${stepCircleClass(step, s.id)}`}>
                {step > s.id ? <CheckCircle2 className="w-4 h-4" /> : s.id}
              </span>
              <span className="text-xs font-semibold uppercase tracking-wide hidden sm:inline">{s.label}</span>
            </div>
            {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 mx-3 ${step > s.id ? "bg-[#009900]" : "bg-[#d5ddd5]"}`} />}
          </div>
        ))}
      </div>

      <div className="bg-white rounded-lg border border-[#e2ebe4] p-6">
        {step === 1 && (
          <div className="space-y-4 fade-up">
            <h2 className="heading-upper text-sm tracking-widest text-[#144733]">Main driver details</h2>
            <div>
              <label className="field-label">Driving license number *</label>
              <input className="field-input" value={form.license_no} onChange={(e) => setForm({ ...form, license_no: e.target.value })} data-testid="checkin-license-input" />
            </div>
            <div>
              <label className="field-label">Nationality *</label>
              <input className="field-input" placeholder="e.g. United Arab Emirates" value={form.nationality} onChange={(e) => setForm({ ...form, nationality: e.target.value })} data-testid="checkin-nationality-input" />
            </div>
            <div>
              <label className="field-label">Date of birth (optional)</label>
              <input type="date" className="field-input" max={dayjs().subtract(21, "year").format("YYYY-MM-DD")} value={form.date_of_birth} onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })} data-testid="checkin-dob-input" />
            </div>
            <button onClick={() => setStep(2)} disabled={!canNext1} className="btn-cta rounded px-6 py-3 text-sm" data-testid="checkin-next-btn">Continue</button>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4 fade-up">
            <h2 className="heading-upper text-sm tracking-widest text-[#144733]">Rental terms & conditions</h2>
            <div className="border border-[#e2ebe4] rounded-lg p-4 max-h-56 overflow-y-auto bg-[#fafcfa]">
              <ul className="space-y-2 text-sm text-[#0F1E17]">
                {TERMS.map((t, i) => <li key={t} className="flex gap-2"><span className="text-[#009900] font-bold">{i + 1}.</span>{t}</li>)}
              </ul>
            </div>
            <label className="flex items-start gap-3 cursor-pointer" data-testid="checkin-terms-checkbox">
              <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} className="mt-1 accent-[#009900]" />
              <span className="text-sm text-[#0F1E17]">I have read and accept the rental terms & conditions and confirm the driver details are accurate.</span>
            </label>
            <div className="flex gap-3">
              <button onClick={() => setStep(1)} className="border-2 border-[#d5ddd5] text-[#57685e] rounded px-6 py-3 text-sm uppercase font-medium tracking-wide">Back</button>
              <button onClick={() => setStep(3)} disabled={!accepted} className="btn-cta rounded px-6 py-3 text-sm" data-testid="checkin-terms-next-btn">Continue to signature</button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4 fade-up">
            <h2 className="heading-upper text-sm tracking-widest text-[#144733]">Digital signature</h2>
            <p className="text-xs text-[#57685e]">Your signature will be attached to rental agreement (Kiosk API — mocked in demo).</p>
            <SignaturePad onChange={setSignature} />
            <div className="flex gap-3">
              <button onClick={() => setStep(2)} className="border-2 border-[#d5ddd5] text-[#57685e] rounded px-6 py-3 text-sm uppercase font-medium tracking-wide">Back</button>
              <button onClick={submit} disabled={!signature || submitting} className="btn-cta rounded px-6 py-3 text-sm flex items-center gap-2" data-testid="checkin-submit-btn">
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />} Sign & complete check-in
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
